import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { BasicModal, DefaultButton, LightBoxImage, LightBoxModal } from 'common/components';
import documentIcon from 'common/images/documentDownload.png';
import pdfIcon from 'common/images/pdf.svg';
import { getBankProofFile } from './api';

const BankProofDocument = ({ userId, proofId, filename }) => {
  const [file, setFile] = useState(null);
  const [failed, setFailed] = useState(false);
  const [opened, setOpened] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    let url;
    setFile(null);
    setFailed(false);
    setOpened(false);
    getBankProofFile(userId, proofId)
      .then(response => {
        if (!active) return;
        if (response.status >= 300) throw response;
        url = URL.createObjectURL(response.data);
        setFile({ url, type: response.data.type });
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [userId, proofId, attempt]);

  if (failed) {
    return (
      <div className="BankProofDocument BankProofDocument--error">
        <p role="alert">
          <FormattedMessage id="bankProofRequestFailed" />
        </p>
        <DefaultButton textId="justRetry" onClick={() => setAttempt(value => value + 1)} />
      </div>
    );
  }

  if (!file) {
    return (
      <div className="BankProofDocument" role="status">
        <FormattedMessage id="justLoading" />
      </div>
    );
  }

  const isImage = ['image/png', 'image/jpeg'].includes(file.type);
  const isPdf = file.type === 'application/pdf';

  return (
    <div className="BankProofDocument">
      {isImage && (
        <LightBoxImage
          className="BankProofDocument__image"
          image={file.url}
          titleId={filename}
          openLightBox={() => setOpened(true)}
        />
      )}
      {isPdf && (
        <button className="BankProofDocument__file" type="button" onClick={() => setOpened(true)} aria-label={filename}>
          <img src={pdfIcon} alt="" />
        </button>
      )}
      {!isImage && !isPdf && (
        <a className="BankProofDocument__file" href={file.url} download={filename} aria-label={filename}>
          <img src={documentIcon} alt="" />
        </a>
      )}
      {opened && isImage && (
        <LightBoxModal images={[file.url]} currentImage={0} onRequestClose={() => setOpened(false)} />
      )}
      {opened && isPdf && (
        <BasicModal
          isOpen
          caption={filename}
          modalClassName="BankProofDocument__modal"
          onRequestClose={() => setOpened(false)}>
          <object className="BankProofDocument__pdf" data={file.url} type="application/pdf" aria-label={filename}>
            <a href={file.url} target="_blank" rel="noopener noreferrer">
              <FormattedMessage id="justOpen" />
            </a>
          </object>
        </BasicModal>
      )}
    </div>
  );
};

BankProofDocument.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  proofId: PropTypes.number.isRequired,
  filename: PropTypes.string.isRequired,
};

export default BankProofDocument;
